import React from "react";
import { mutate } from "swr";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/app/DatePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, LockSimple } from "@phosphor-icons/react";

export default function Settings({ file, years, journals, fileId }) {
  const [form, setForm] = React.useState({
    name: file.name, currency: file.currency,
    tva_rate: String(file.tva_rate), regime_fiscal: file.regime_fiscal || "reel_normal",
    accounting_mode: file.accounting_mode || "engagement",
    is_acompte_rate: String(file.is_acompte_rate ?? 2.2),
  });
  const [jOpen, setJOpen] = React.useState(false);
  const [jForm, setJForm] = React.useState({ code: "", name: "", type: "od" });
  const [yOpen, setYOpen] = React.useState(false);
  const [yForm, setYForm] = React.useState({ label: "", start: "", end: "" });

  const saveFile = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/accounting/files/${fileId}`, {
        name: form.name, currency: form.currency,
        tva_rate: parseFloat(form.tva_rate), regime_fiscal: form.regime_fiscal,
        accounting_mode: form.accounting_mode, is_acompte_rate: parseFloat(form.is_acompte_rate),
      });
      toast.success("Paramètres enregistrés");
      mutate(`/accounting/files/${fileId}`);
    } catch { toast.error("Enregistrement impossible"); }
  };

  const addJournal = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/accounting/files/${fileId}/journals`, jForm);
      toast.success("Journal créé");
      setJOpen(false); setJForm({ code: "", name: "", type: "od" });
      mutate(`/accounting/files/${fileId}/journals`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Création impossible"); }
  };

  const addYear = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/accounting/files/${fileId}/years`, yForm);
      toast.success("Exercice créé");
      setYOpen(false); setYForm({ label: "", start: "", end: "" });
      mutate(`/accounting/files/${fileId}/years`);
    } catch { toast.error("Création impossible"); }
  };

  const closeYear = async (yid) => {
    await api.put(`/accounting/files/${fileId}/years/${yid}/close`);
    toast.success("Exercice clôturé");
    mutate(`/accounting/files/${fileId}/years`);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <form onSubmit={saveFile} className="border border-zinc-200 bg-white rounded-md p-6 space-y-4">
        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Paramètres du dossier</div>
        <div><Label>Nom du dossier</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="settings-name" /></div>
        <div>
          <Label>Devise</Label>
          <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="XAF">FCFA (XAF)</SelectItem><SelectItem value="EUR">Euro (EUR)</SelectItem></SelectContent>
          </Select>
        </div>
        <div><Label>Taux TVA (%)</Label><Input type="number" step="0.01" value={form.tva_rate} onChange={(e) => setForm({ ...form, tva_rate: e.target.value })} data-testid="settings-tva" /></div>
        <div>
          <Label>Régime fiscal</Label>
          <Select value={form.regime_fiscal} onValueChange={(v) => setForm({ ...form, regime_fiscal: v })}>
            <SelectTrigger data-testid="settings-regime"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="reel_normal">Réel normal</SelectItem>
              <SelectItem value="reel_simplifie">Réel simplifié</SelectItem>
              <SelectItem value="liberatoire">Libératoire</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Type de tenue comptable</Label>
          <Select value={form.accounting_mode} onValueChange={(v) => setForm({ ...form, accounting_mode: v })}>
            <SelectTrigger data-testid="settings-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="engagement">Système normal (créances / dettes)</SelectItem>
              <SelectItem value="tresorerie">Système minimal de trésorerie (SMT)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[11px] text-zinc-400 mt-1">En SMT, seules les opérations encaissées/décaissées sont saisies ; les créances et dettes sont régularisées à la clôture.</p>
        </div>
        <div>
          <Label>Taux d'acompte IS (%)</Label>
          <Select value={form.is_acompte_rate} onValueChange={(v) => setForm({ ...form, is_acompte_rate: v })}>
            <SelectTrigger data-testid="settings-is-rate"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="2.2">2,2 % (régime réel normal)</SelectItem>
              <SelectItem value="5.5">5,5 % (régime réel simplifié)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button type="submit" className="bg-[#2E3192] hover:bg-[#1E2066]" data-testid="settings-save">Enregistrer</Button>
      </form>

      <div className="border border-zinc-200 bg-white rounded-md p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Journaux</div>
          <Dialog open={jOpen} onOpenChange={setJOpen}>
            <DialogTrigger asChild><Button variant="outline" size="sm" data-testid="add-journal-btn"><Plus size={14} className="mr-1" /> Ajouter</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-serif-display text-2xl">Nouveau journal</DialogTitle><DialogDescription>Code court (2-4 lettres) et type fonctionnel.</DialogDescription></DialogHeader>
              <form onSubmit={addJournal} className="space-y-3">
                <div><Label htmlFor="journal-code">Code</Label><Input id="journal-code" required maxLength={4} value={jForm.code} onChange={(e) => setJForm({ ...jForm, code: e.target.value.toUpperCase() })} data-testid="journal-code" placeholder="BQ2" /></div>
                <div><Label htmlFor="journal-name">Intitulé</Label><Input id="journal-name" required value={jForm.name} onChange={(e) => setJForm({ ...jForm, name: e.target.value })} placeholder="Banque secondaire" data-testid="journal-name" /></div>
                <div>
                  <Label htmlFor="journal-type">Type</Label>
                  <Select value={jForm.type} onValueChange={(v) => setJForm({ ...jForm, type: v })}>
                    <SelectTrigger id="journal-type" data-testid="journal-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="achats">Achats</SelectItem><SelectItem value="ventes">Ventes</SelectItem>
                      <SelectItem value="banque">Banque</SelectItem><SelectItem value="paie">Paie</SelectItem>
                      <SelectItem value="od">Opérations diverses</SelectItem><SelectItem value="an">À-nouveaux</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end"><Button type="submit" className="bg-[#2E3192] hover:bg-[#1E2066]" data-testid="journal-submit">Créer</Button></div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        <table className="w-full text-sm">
          <tbody className="divide-y divide-zinc-100">
            {journals.map((j) => (
              <tr key={j.id} data-testid={`journal-row-${j.code}`}>
                <td className="py-2 font-mono text-xs w-16">{j.code}</td>
                <td className="py-2">{j.name}</td>
                <td className="py-2 text-zinc-500 text-xs capitalize">{j.type}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="border border-zinc-200 bg-white rounded-md p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Exercices</div>
          <Dialog open={yOpen} onOpenChange={setYOpen}>
            <DialogTrigger asChild><Button variant="outline" size="sm" data-testid="add-year-btn"><Plus size={14} className="mr-1" /> Ajouter</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-serif-display text-2xl">Nouvel exercice</DialogTitle><DialogDescription>Période comptable du dossier.</DialogDescription></DialogHeader>
              <form onSubmit={addYear} className="space-y-3">
                <div><Label htmlFor="year-label">Libellé</Label><Input id="year-label" required value={yForm.label} onChange={(e) => setYForm({ ...yForm, label: e.target.value })} placeholder="Exercice 2027" data-testid="year-label" /></div>
                <div><Label>Début</Label><DatePicker value={yForm.start} onChange={(v) => setYForm({ ...yForm, start: v })} testid="year-start" /></div>
                <div><Label>Fin</Label><DatePicker value={yForm.end} onChange={(v) => setYForm({ ...yForm, end: v })} testid="year-end" /></div>
                <div className="flex justify-end"><Button type="submit" className="bg-[#2E3192] hover:bg-[#1E2066]" data-testid="year-submit">Créer</Button></div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        <table className="w-full text-sm">
          <tbody className="divide-y divide-zinc-100">
            {years.map((y) => (
              <tr key={y.id} data-testid={`year-row-${y.id}`}>
                <td className="py-2">{y.label}</td>
                <td className="py-2 text-zinc-500 text-xs">{y.start} → {y.end}</td>
                <td className="py-2 text-right">
                  {y.status === "ouvert"
                    ? <button onClick={() => closeYear(y.id)} className="text-xs text-zinc-600 hover:text-red-800 inline-flex items-center gap-1" data-testid={`close-year-${y.id}`}><LockSimple size={12} /> Clôturer</button>
                    : <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600">Clôturé</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
